import { useState } from "react";
import {
  useStartRecurlySubscriptionMutation,
  useReactivateRecurlySubscriptionMutation,
  ReactivateRecurlySubscriptionMutation,
  GetRecurlyBillingDataDocument,
  UsesRecurlySubscriptionDocument,
} from "@ugitgud/legos/queries/lol";
import { CAPTCHA_ACTIONS, executeCaptcha, isCaptchaCancellation, type CaptchaAction } from "lib/captcha";
import {
  useAddPaymentMethod as useAddPaymentMethodBase,
  useSetDefaultPaymentMethod as useSetDefaultPaymentMethodBase,
  type CaptchaCredentials,
} from "@ugitgud/legos/hooks/usePremiumHooks";

// Re-export the hooks that take no captcha. The two that do are wrapped below.
export {
  usePremiumPlanPrices,
  useBillingData,
  useCancelSubscription,
  useDeletePaymentMethod,
  useRecurlySubscriptionStatus,
} from "@ugitgud/legos/hooks/usePremiumHooks";

interface StartSubscriptionResponse {
  success: boolean;
  error?: string | null;
  subscription?: {
    status?: string | null;
    cancelAtPeriodEnd?: boolean | null;
    currentPeriodEnd?: number | null;
  } | null;
}

interface HookCallbacks<T> {
  onCompleted?: (data: T) => void;
  onError?: (error: string) => void;
}

// Provider-neutral: the user doesn't care which vendor scored them, and the wording
// shouldn't need changing when the provider flips.
const CAPTCHA_ERROR_MESSAGE = "We couldn't verify your request. Please try again.";

/**
 * Message to show for a failed captcha, or null when there is nothing to say —
 * a visitor who dismissed the challenge themselves already knows why nothing
 * happened, so that case returns the form to idle rather than accusing them.
 */
function captchaFailureMessage(err: unknown, action: CaptchaAction): string | null {
  if (isCaptchaCancellation(err)) return null;
  console.error(`Captcha failed for ${action}`, err);
  return CAPTCHA_ERROR_MESSAGE;
}

/**
 * Wraps a legos payment hook so it mints a fresh token immediately before its
 * mutation.
 *
 * Minting at call time rather than holding a widget open is what keeps tokens
 * valid: they are single-use and expire after 300s, so a token is only ever
 * seconds old here, and a retry after a failure necessarily mints a new one.
 */
function useCaptchaGuardedPayment(
  action: CaptchaAction,
  send: (recurlyToken: string, captcha?: CaptchaCredentials) => void,
  isSending: boolean,
  sendError: string | null,
  onError?: (error: string) => void,
) {
  const [isMinting, setIsMinting] = useState(false);
  const [captchaError, setCaptchaError] = useState<string | null>(null);

  const run = async (recurlyToken: string): Promise<boolean> => {
    setIsMinting(true);
    setCaptchaError(null);

    let captcha: CaptchaCredentials;
    try {
      captcha = await executeCaptcha(action);
    } catch (err) {
      setIsMinting(false);
      const message = captchaFailureMessage(err, action);
      setCaptchaError(message);
      if (message) onError?.(message);
      return false;
    }

    setIsMinting(false);
    send(recurlyToken, captcha);
    return true;
  };

  return { run, isLoading: isMinting || isSending, error: captchaError ?? sendError };
}

type AddPaymentMethodCallbacks = Parameters<typeof useAddPaymentMethodBase>[0];

/**
 * `updateRecurlyPaymentMethod` — the only call in the flow that reaches the card
 * issuer, since attaching billing info triggers a $0 gateway verification.
 *
 * Both payment surfaces (checkout and `/settings/plus`) import this hook, so one
 * wrapper covers them together.
 */
export const useAddPaymentMethod = (callbacks: AddPaymentMethodCallbacks = {}) => {
  const { addPaymentMethod: send, isLoading: isSending, error: sendError } = useAddPaymentMethodBase(callbacks);

  const { run, isLoading, error } = useCaptchaGuardedPayment(
    CAPTCHA_ACTIONS.addPaymentMethod,
    send,
    isSending,
    sendError,
    callbacks?.onError,
  );

  return { addPaymentMethod: run, isLoading, error };
};

type SetDefaultPaymentMethodCallbacks = Parameters<typeof useSetDefaultPaymentMethodBase>[0];

export const useSetDefaultPaymentMethod = (callbacks: SetDefaultPaymentMethodCallbacks = {}) => {
  const { setDefaultPaymentMethod: send, isLoading: isSending, error: sendError } = useSetDefaultPaymentMethodBase(callbacks);

  const { run, isLoading, error } = useCaptchaGuardedPayment(
    CAPTCHA_ACTIONS.addPaymentMethod,
    send,
    isSending,
    sendError,
    callbacks?.onError,
  );

  return { setDefaultPaymentMethod: run, isLoading, error };
};

export const useStartSubscription = ({ onCompleted, onError }: HookCallbacks<StartSubscriptionResponse> = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [startSubscriptionMutation] = useStartRecurlySubscriptionMutation({
    context: { clientName: "lol" },
    refetchQueries: [
      { query: GetRecurlyBillingDataDocument, context: { clientName: "lol" } },
      { query: UsesRecurlySubscriptionDocument, context: { clientName: "lol" } },
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      setIsLoading(false);
      if (data.premiumSignupRecurly?.success) {
        onCompleted?.(data.premiumSignupRecurly);
      } else {
        const errorMsg = data.premiumSignupRecurly?.error || "An error occurred";
        setError(errorMsg);
        onError?.(errorMsg);
      }
    },
    onError: (err) => {
      setIsLoading(false);
      const errorMsg = err.message || "An error occurred while processing your subscription";
      setError(errorMsg);
      onError?.(errorMsg);
    },
  });

  const startSubscription = async (recurlyToken: string, planId: string) => {
    setIsLoading(true);
    setError(null);

    let captcha;
    try {
      captcha = await executeCaptcha(CAPTCHA_ACTIONS.checkoutSignup);
    } catch (err) {
      setIsLoading(false);
      const message = captchaFailureMessage(err, CAPTCHA_ACTIONS.checkoutSignup);
      setError(message);
      if (message) onError?.(message);
      return;
    }

    startSubscriptionMutation({
      variables: {
        planId,
        recurlyToken,
        recaptchaToken: captcha.token,
        recaptchaVersion: captcha.version,
      },
    });
  };

  return { startSubscription, isLoading, error };
};

type ReactivateSubscriptionResponse = NonNullable<ReactivateRecurlySubscriptionMutation["reactivateRecurlySubscription"]>;

export const useReactivateSubscription = ({ onCompleted, onError }: HookCallbacks<ReactivateSubscriptionResponse> = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [reactivateSubscriptionMutation] = useReactivateRecurlySubscriptionMutation({
    context: { clientName: "lol" },
    refetchQueries: [
      { query: GetRecurlyBillingDataDocument, context: { clientName: "lol" } },
      { query: UsesRecurlySubscriptionDocument, context: { clientName: "lol" } },
    ],
    onCompleted: (data) => {
      setIsLoading(false);
      if (data.reactivateRecurlySubscription?.success) {
        onCompleted?.(data.reactivateRecurlySubscription);
      } else {
        const errorMsg = data.reactivateRecurlySubscription?.error || "An error occurred";
        setError(errorMsg);
        onError?.(errorMsg);
      }
    },
    onError: (err) => {
      setIsLoading(false);
      const errorMsg = err.message || "An error occurred while reactivating your subscription";
      setError(errorMsg);
      onError?.(errorMsg);
    },
  });

  const reactivateSubscription = async (recurlyToken: string) => {
    setIsLoading(true);
    setError(null);

    let captcha;
    try {
      captcha = await executeCaptcha(CAPTCHA_ACTIONS.reactivateSubscription);
    } catch (err) {
      setIsLoading(false);
      const message = captchaFailureMessage(err, CAPTCHA_ACTIONS.reactivateSubscription);
      setError(message);
      if (message) onError?.(message);
      return;
    }

    reactivateSubscriptionMutation({
      variables: {
        recurlyToken,
        recaptchaToken: captcha.token,
        recaptchaVersion: captcha.version,
      },
    });
  };

  return { reactivateSubscription, isLoading, error };
};
